import { useState, useEffect, useCallback } from "react";
import { Receipt, FileSpreadsheet, FileText, Send, RotateCcw, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { formatFecha } from "../../lib/dates";
import { Table } from "../../components/ui/Table";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Pagination } from "../../components/ui/Pagination";
import { Modal } from "../../components/ui/Modal";
import { Badge } from "../../components/ui/Badge";
import { ClienteSearchSelect } from "../../components/admin/ClienteSearchSelect";

interface ReciboRow {
  id: number;
  numeroRecibo: string;
  fecha: string;
  tipo: string;
  importePrima: number;
  gastosGestion: number;
  importeTotal: number;
  seguroId: number;
  eventoReferencia: string | null;
  tienePdf: boolean;
  tieneExcel: boolean;
  razonSocial?: string;
  envioEstado: string;
  enviadoAt: string | null;
  claseDocumento: string;
  documentoEstado: string;
  rectificaReciboId: number | null;
  motivoRectificacion: string | null;
}

interface AnulacionPreview {
  reciboId: number;
  numeroRecibo: string;
  importeTotal: number;
  puedeRectificarse: boolean;
  problemas: string[];
  cobros: Array<{
    id: number;
    orderId: string | null;
    importeTotal: number;
    estado: string;
    devolucionEstado: string;
    pilotos: number;
    puedeDevolverseAutomaticamente: boolean;
    problema: string | null;
  }>;
}

interface RecibosListResponse {
  data: ReciboRow[];
  total: number;
  page: number;
  limit: number;
}

type Filters = {
  cliente_id: string;
  desde: string;
  hasta: string;
};

const EMPTY_FILTERS: Filters = { cliente_id: "", desde: "", hasta: "" };

function formatEuro(value: number): string {
  return `${Number(value).toFixed(2)} €`;
}

function tipoLabel(tipo: string): string {
  return tipo === "mensual" ? "Mensual" : "Individual";
}

function envioLabel(estado: string): string {
  if (estado === "enviado") return "Enviado";
  if (estado === "error") return "Error";
  if (estado === "enviando") return "Enviando";
  if (estado === "pendiente") return "Pendiente de revisión";
  return "Histórico";
}

function paqueteLabel(eventoReferencia: string | null): string {
  const ref = eventoReferencia?.trim();
  return ref || "Sin paquete";
}

async function downloadFile(url: string, filename: string, mime: string) {
  const response = await api.get(url, { responseType: "blob" });
  const blob = new Blob([response.data as BlobPart], { type: mime });
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function RecibosPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [rows, setRows] = useState<ReciboRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [cancelRow, setCancelRow] = useState<ReciboRow | null>(null);
  const [cancelPreview, setCancelPreview] = useState<AnulacionPreview | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelPreviewLoading, setCancelPreviewLoading] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelMessage, setCancelMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit };
      if (appliedFilters.cliente_id) params.cliente_id = appliedFilters.cliente_id;
      if (appliedFilters.desde) params.desde = appliedFilters.desde;
      if (appliedFilters.hasta) params.hasta = appliedFilters.hasta;
      const res = await api.get<RecibosListResponse>("/admin/recibos", { params });
      setRows(res.data.data ?? []);
      setTotal(res.data.total ?? 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, appliedFilters]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const handleExport = async () => {
    setExportError(null);
    setExportLoading(true);
    try {
      const search = new URLSearchParams();
      if (appliedFilters.cliente_id) search.set("cliente_id", appliedFilters.cliente_id);
      if (appliedFilters.desde) search.set("desde", appliedFilters.desde);
      if (appliedFilters.hasta) search.set("hasta", appliedFilters.hasta);
      const qs = search.toString();
      await downloadFile(
        `/admin/recibos/export${qs ? `?${qs}` : ""}`,
        `recibos-${new Date().toISOString().slice(0, 10)}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    } catch {
      setExportError("Error al exportar el Excel. Inténtalo de nuevo.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadPdf = async (row: ReciboRow) => {
    setDownloadError(null);
    try {
      await downloadFile(
        `/admin/recibos/${row.id}/descargar/pdf`,
        `recibo-${row.numeroRecibo.replace("/", "-")}.pdf`,
        "application/pdf"
      );
    } catch {
      setDownloadError("No se pudo descargar el PDF.");
    }
  };

  const handleDownloadExcel = async (row: ReciboRow) => {
    setDownloadError(null);
    try {
      await downloadFile(
        `/admin/recibos/${row.id}/descargar/excel`,
        `recibo-${row.numeroRecibo.replace("/", "-")}.xlsx`,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
    } catch {
      setDownloadError("No se pudo descargar el Excel.");
    }
  };

  const handleApproveAndSend = async (row: ReciboRow) => {
    const confirmed = window.confirm(
      `¿Aprobar y enviar el recibo ${row.numeroRecibo} al cliente?`
    );
    if (!confirmed) return;

    setSendError(null);
    setSendingId(row.id);
    try {
      await api.post(`/admin/recibos/${row.id}/aprobar-enviar`);
      await loadList();
    } catch {
      setSendError(
        "No se pudo enviar el recibo. Se mantiene oculto para el cliente y puede reintentarse."
      );
      await loadList();
    } finally {
      setSendingId(null);
    }
  };

  const openCancellation = async (row: ReciboRow) => {
    setCancelRow(row);
    setCancelPreview(null);
    setCancelReason("");
    setCancelMessage(null);
    setCancelPreviewLoading(true);
    try {
      const res = await api.get<AnulacionPreview>(
        `/admin/recibos/${row.id}/anulacion-preview`
      );
      setCancelPreview(res.data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setCancelMessage({
        ok: false,
        text: ax.response?.data?.error ?? "No se pudo comprobar si el recibo puede anularse.",
      });
    } finally {
      setCancelPreviewLoading(false);
    }
  };

  const handleCancellation = async () => {
    if (!cancelRow || !cancelPreview?.puedeRectificarse) return;
    setCancelLoading(true);
    setCancelMessage(null);
    try {
      const res = await api.post<{
        rectificativa: { numeroRecibo: string };
        devoluciones: Array<{ resultado: { status: string } }>;
      }>(`/admin/recibos/${cancelRow.id}/anular`, { motivo: cancelReason });
      const fallidas = res.data.devoluciones.filter(
        (item) =>
          item.resultado.status !== "confirmada" &&
          item.resultado.status !== "ya_devuelto" &&
          item.resultado.status !== "sin_pasarela"
      ).length;
      setCancelMessage({
        ok: fallidas === 0,
        text:
          fallidas === 0
            ? `Rectificativa ${res.data.rectificativa.numeroRecibo} creada y devoluciones confirmadas.`
            : `Rectificativa ${res.data.rectificativa.numeroRecibo} creada. ${fallidas} devolución(es) requieren revisión en Cobros.`,
      });
      await loadList();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      setCancelMessage({
        ok: false,
        text: ax.response?.data?.error ?? "No se pudo anular el recibo.",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  const columns = [
    { key: "numero", header: "Número", render: (r: ReciboRow) => r.numeroRecibo },
    { key: "fecha", header: "Fecha", render: (r: ReciboRow) => formatFecha(r.fecha) },
    {
      key: "tipo",
      header: "Tipo",
      render: (r: ReciboRow) =>
        r.claseDocumento === "rectificativo" ? "Rectificativo" : tipoLabel(r.tipo),
    },
    {
      key: "cliente",
      header: "Cliente",
      render: (r: ReciboRow) => r.razonSocial?.trim() || "—",
    },
    { key: "paquete", header: "Paquete", render: (r: ReciboRow) => paqueteLabel(r.eventoReferencia) },
    { key: "prima", header: "Prima", render: (r: ReciboRow) => formatEuro(r.importePrima) },
    {
      key: "gastos",
      header: "Gastos",
      render: (r: ReciboRow) => formatEuro(r.gastosGestion),
    },
    { key: "total", header: "Total", render: (r: ReciboRow) => formatEuro(r.importeTotal) },
    { key: "envio", header: "Envío", render: (r: ReciboRow) => envioLabel(r.envioEstado) },
    {
      key: "estadoDocumento",
      header: "Documento",
      render: (r: ReciboRow) =>
        r.documentoEstado === "rectificado" ? (
          <Badge variant="warning">Rectificado</Badge>
        ) : r.claseDocumento === "rectificativo" ? (
          <Badge variant="info">Rectificativo</Badge>
        ) : (
          <Badge variant="success">Vigente</Badge>
        ),
    },
    {
      key: "acciones",
      header: "Acciones",
      render: (r: ReciboRow) => (
        <div className="flex flex-wrap gap-1">
          {r.tienePdf && (
            <Button size="sm" variant="secondary" onClick={() => void handleDownloadPdf(r)}>
              PDF
            </Button>
          )}
          {r.tieneExcel && (
            <Button size="sm" variant="secondary" onClick={() => void handleDownloadExcel(r)}>
              Excel
            </Button>
          )}
          {r.documentoEstado === "vigente" &&
            (r.envioEstado === "pendiente" || r.envioEstado === "error") && (
            <Button
              size="sm"
              loading={sendingId === r.id}
              onClick={() => void handleApproveAndSend(r)}
            >
              <Send className="h-4 w-4" />
              Aprobar y enviar
            </Button>
          )}
          {r.claseDocumento === "ordinario" &&
            r.documentoEstado === "vigente" &&
            r.tipo === "mensual" && (
              <Button size="sm" variant="danger" onClick={() => void openCancellation(r)}>
                <RotateCcw className="h-4 w-4" />
                Anular
              </Button>
            )}
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[#1A3AB8]/10 p-2">
            <Receipt className="h-6 w-6 text-[#1A3AB8]" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-[#2C2C2C]">Recibos</h1>
            <p className="text-sm text-gray-500">Listado de recibos de todos los organizadores</p>
          </div>
        </div>
        <Button variant="secondary" loading={exportLoading} onClick={() => void handleExport()}>
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          Exportar Excel
        </Button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px]">
            <ClienteSearchSelect
              label="Cliente"
              value={filters.cliente_id}
              onChange={(value) =>
                setFilters((f) => ({ ...f, cliente_id: value }))
              }
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Desde</label>
            <Input
              type="date"
              value={filters.desde}
              onChange={(e) => setFilters((f) => ({ ...f, desde: e.target.value }))}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-600">Hasta</label>
            <Input
              type="date"
              value={filters.hasta}
              onChange={(e) => setFilters((f) => ({ ...f, hasta: e.target.value }))}
            />
          </div>
          <Button onClick={handleApplyFilters}>Filtrar</Button>
          <Button variant="secondary" onClick={handleClearFilters}>
            Limpiar
          </Button>
        </div>
      </div>

      {exportError && <p className="text-sm text-red-600">{exportError}</p>}
      {downloadError && <p className="text-sm text-red-600">{downloadError}</p>}
      {sendError && <p className="text-sm text-red-600">{sendError}</p>}

      {loading ? (
        <p className="text-gray-500">Cargando recibos…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center">
          <FileText className="mx-auto mb-3 h-10 w-10 text-gray-300" />
          <p className="text-gray-500">No hay recibos para los filtros seleccionados.</p>
        </div>
      ) : (
        <>
          <Table columns={columns} data={rows} />
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </>
      )}

      <Modal
        isOpen={cancelRow != null}
        onClose={() => {
          if (!cancelLoading) setCancelRow(null);
        }}
        title={`Anular recibo ${cancelRow?.numeroRecibo ?? ""}`}
        size="lg"
        footer={
          <>
            <Button variant="secondary" disabled={cancelLoading} onClick={() => setCancelRow(null)}>
              Cerrar
            </Button>
            <Button
              variant="danger"
              loading={cancelLoading}
              disabled={
                !cancelPreview?.puedeRectificarse ||
                cancelReason.trim().length < 5 ||
                Boolean(cancelMessage?.ok)
              }
              onClick={() => void handleCancellation()}
            >
              Crear rectificativa y devolver
            </Button>
          </>
        }
      >
        {cancelPreviewLoading ? (
          <p className="text-gray-500">Comprobando trazabilidad del recibo…</p>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>
                  Se conservará el original, se creará una rectificativa por el importe negativo y
                  se solicitará a Redsys la devolución total de cada cobro asociado. Los pilotos
                  solo volverán a estar pendientes cuando su devolución quede confirmada.
                </p>
              </div>
            </div>

            {cancelPreview && (
              <>
                <p className="text-sm">
                  Importe del recibo: <strong>{formatEuro(cancelPreview.importeTotal)}</strong>
                </p>
                {cancelPreview.problemas.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <p className="font-medium">No puede automatizarse:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {cancelPreview.problemas.map((problema) => (
                        <li key={problema}>{problema}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {cancelPreview.cobros.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                      <thead>
                        <tr className="border-b text-gray-500">
                          <th className="py-2">Pedido</th>
                          <th>Importe</th>
                          <th>Pilotos</th>
                          <th>Comprobación</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cancelPreview.cobros.map((cobro) => (
                          <tr key={cobro.id} className="border-b">
                            <td className="py-2">{cobro.orderId ?? `Cobro #${cobro.id}`}</td>
                            <td>{formatEuro(cobro.importeTotal)}</td>
                            <td>{cobro.pilotos}</td>
                            <td>
                              {cobro.puedeDevolverseAutomaticamente ? (
                                <Badge variant="success">Preparado</Badge>
                              ) : (
                                <span className="text-red-600">{cobro.problema}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Motivo de la rectificación
              </label>
              <textarea
                className="min-h-24 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={cancelReason}
                disabled={!cancelPreview?.puedeRectificarse || cancelLoading}
                onChange={(event) => setCancelReason(event.target.value)}
                placeholder="Ej.: importe incorrecto en el recibo original"
              />
            </div>

            {cancelMessage && (
              <div
                className={`rounded-lg border p-3 text-sm ${
                  cancelMessage.ok
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-red-200 bg-red-50 text-red-800"
                }`}
              >
                {cancelMessage.text}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
